import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

const PROTEIN_OPTION_NAMES = ['protein', 'flavor', 'flavour', 'recipe'];

/**
 * Rewrites each variant-option fieldset legend into structured label + value
 * spans, and marks protein/flavor fieldsets with the dot-color class.
 * Mirrors the logic in instinct-variant-picker-styles.liquid's inline script,
 * but scoped to any container (used for the quick-add drawer).
 * @param {Element} container
 */
function setupVariantPickerLegends(container) {
  container.querySelectorAll('fieldset.variant-option--buttons').forEach((fieldset) => {
    const legend = fieldset.querySelector('legend');
    if (!legend) return;

    let valueSpan = legend.querySelector('.variant-option__legend-value');

    if (!valueSpan) {
      const labelText = (legend.dataset.instinctLabel || legend.textContent || '').trim();
      legend.dataset.instinctLabel = labelText;
      legend.textContent = '';

      const labelSpan = document.createElement('span');
      labelSpan.className = 'variant-option__legend-label';
      labelSpan.textContent = labelText;
      valueSpan = document.createElement('span');
      valueSpan.className = 'variant-option__legend-value';
      legend.append(labelSpan, valueSpan);

      if (PROTEIN_OPTION_NAMES.includes(labelText.toLowerCase())) {
        fieldset.classList.add('variant-option--has-protein-dot');
      }
    }

    const checked = fieldset.querySelector('input[type="radio"]:checked');
    const nextText = checked ? `: ${checked.value}` : '';
    if (valueSpan.textContent !== nextText) valueSpan.textContent = nextText;
  });
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      // Run AFTER #updateVariantPicker so it doesn't overwrite the legend spans
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) setupVariantPickerLegends(modalContent);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const modalContent = dialogComponent.querySelector('#quick-add-modal-content');
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Builds the unified drawer layout and morphs it into the modal content.
   * Same structure on both mobile and desktop.
   * @param {Element} productGrid - The product grid element from the fetched product page
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    // Capture per-product dot-color <style> elements emitted by
    // instinct-variant-picker-styles.liquid — these are siblings of variant-picker
    // inside the block output (nested inside the details column), not direct children
    // of productGrid, so we query the full subtree rather than :scope > style.
    const dotStyles = [...productGrid.querySelectorAll('style')];

    // Extract key elements before clearing the grid
    const variantPicker = productGrid.querySelector('variant-picker');
    const instinctForm = productGrid.querySelector('.instinct-product-form');
    const speciesTagEl = productGrid.querySelector('.instinct-species-tag');

    // Fallback form if no instinct-product-form block is present
    const fallbackForm = !instinctForm ? productGrid.querySelector('.buy-buttons-block') : null;

    // Get the product thumbnail from the product card (already loaded — no extra fetch)
    const productCard = this.closest('product-card');
    const cardImg = productCard?.querySelector('img[src]');

    // Replace productGrid children with the new unified layout (keep its root attrs so morph behaves consistently)
    productGrid.innerHTML = '';

    // — Product header: thumbnail + title ——————————————————————
    const productHeader = document.createElement('div');
    productHeader.classList.add('quick-add-product-header');

    if (cardImg) {
      const thumbDiv = document.createElement('div');
      thumbDiv.classList.add('quick-add-product-thumb');
      const thumbImg = new Image();
      thumbImg.src = cardImg.currentSrc || cardImg.src;
      thumbImg.alt = cardImg.alt || this.dataset.productTitle || '';
      thumbImg.loading = 'eager';
      thumbDiv.appendChild(thumbImg);
      productHeader.appendChild(thumbDiv);
    }

    const infoDiv = document.createElement('div');
    infoDiv.classList.add('quick-add-product-info');

    const titleLink = document.createElement('a');
    titleLink.href = this.productPageUrl;
    titleLink.textContent = this.dataset.productTitle || '';
    titleLink.classList.add('quick-add-product-title');
    infoDiv.appendChild(titleLink);

    if (speciesTagEl) infoDiv.appendChild(speciesTagEl);
    productHeader.appendChild(infoDiv);
    productGrid.appendChild(productHeader);

    // — Divider ———————————————————————————————————————————————
    productGrid.appendChild(this.#createDrawerDivider());

    // — Variant picker ————————————————————————————————————————
    if (variantPicker) {
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(this.#createDrawerDivider());
    }

    // — Product form (vendor links or subscribe/add-to-cart) ——
    if (instinctForm) {
      productGrid.appendChild(instinctForm);
    } else if (fallbackForm) {
      productGrid.appendChild(fallbackForm);
    }

    // Re-attach captured dot-color styles so morph carries them into the live DOM
    for (const style of dotStyles) productGrid.appendChild(style);

    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);

    // If morph patched the instinct-product-form element in place (no disconnect/reconnect),
    // connectedCallback won't re-fire and _initialized stays true — meaning the element still
    // holds references to the updated DOM nodes and its listeners survive morph. But when
    // switching products, force a fresh init so cards/buttons are re-queried.
    const orderForm = /** @type {any} */ (modalContent.querySelector('instinct-product-form'));
    if (orderForm && typeof orderForm.connectedCallback === 'function') {
      orderForm._initialized = false;
      orderForm.connectedCallback();
    }
  }

  /**
   * Creates a horizontal divider element for the drawer layout.
   * @returns {HTMLElement}
   */
  #createDrawerDivider() {
    const div = document.createElement('div');
    div.classList.add('quick-add-divider');
    return div;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateVendorButtons);
    this.addEventListener(ThemeEvents.variantUpdate, this.#refreshLegends);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #refreshLegends = () => setupVariantPickerLegends(this);

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    if (!anchorElement) return;

    const drawerTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-product-title'));
    if (drawerTitle) drawerTitle.href = anchorElement.href;
  };

  /**
   * Re-syncs vendor button visibility when the selected variant changes.
   * The instinct-product-form inline script doesn't re-execute after morph,
   * so we replicate the update here using the variant-update event payload.
   * @param {CustomEvent} event
   */
  #updateVendorButtons = (event) => {
    const updatedHtml = /** @type {Element | null} */ (event.detail?.data?.html);
    if (!updatedHtml) return;

    const newVendorSection = updatedHtml.querySelector('[data-vendor-section]');
    const currentVendorSection = /** @type {HTMLElement | null} */ (this.querySelector('[data-vendor-section]'));
    const currentOrderForm = /** @type {HTMLElement | null} */ (this.querySelector('[data-order-form]'));

    if (!currentVendorSection) return;

    const hasVendors = newVendorSection?.classList.contains('instinct-vendor-buttons--visible') ?? false;

    currentVendorSection.classList.toggle('instinct-vendor-buttons--visible', hasVendors);
    currentVendorSection.setAttribute('aria-hidden', hasVendors ? 'false' : 'true');

    if (currentOrderForm) {
      currentOrderForm.classList.toggle('instinct-order-type-wrapper--hidden', hasVendors);
      currentOrderForm.setAttribute('aria-hidden', hasVendors ? 'true' : 'false');
    }

    if (newVendorSection) {
      for (const key of ['amazon', 'chewy', 'instacart']) {
        const newLink = /** @type {HTMLAnchorElement | null} */ (newVendorSection.querySelector(`[data-vendor="${key}"]`));
        const currentLink = /** @type {HTMLAnchorElement | null} */ (currentVendorSection.querySelector(`[data-vendor="${key}"]`));
        if (!newLink || !currentLink) continue;
        currentLink.href = newLink.href;
        currentLink.style.display = newLink.style.display;
      }
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
